import { TrendingUp, Clock, CalendarX, MessageCircle, Users, ShieldCheck } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

/**
 * Semble-inspired bold-number bento. Big before→after outcomes
 * plus supporting metrics — pharma-style, high-contrast.
 */

const STATS = [
  {
    icon: TrendingUp,
    metric: "2×",
    label: { en: "Patient capacity", bn: "রোগী সেবার ক্ষমতা" },
    detail: { en: "Same team, twice the visits — without burnout.", bn: "একই টিম, দ্বিগুণ ভিজিট — ক্লান্তি ছাড়াই।" },
    accent: "#1ba179",
    big: true,
  },
  {
    icon: Clock,
    metric: "3h → 45m",
    label: { en: "Note-writing time", bn: "নোট লেখার সময়" },
    detail: { en: "AI ambient scribe drafts Bengali SOAP notes.", bn: "AI অ্যাম্বিয়েন্ট স্ক্রাইব বাংলা SOAP লেখে।" },
    accent: "#0891b2",
    big: true,
  },
  {
    icon: CalendarX,
    metric: "12% → 4%",
    label: { en: "No-show rate", bn: "অনুপস্থিতির হার" },
    detail: { en: "Automatic WhatsApp reminders 24h before.", bn: "২৪ ঘণ্টা আগে WhatsApp রিমাইন্ডার।" },
    accent: "#16a34a",
  },
  {
    icon: MessageCircle,
    metric: "Same day",
    label: { en: "Patient feedback", bn: "রোগীর ফিডব্যাক" },
    detail: { en: "1-5 star surveys sent right after the visit.", bn: "ভিজিটের পরেই ১-৫ স্টার সার্ভে।" },
    accent: "#7c3aed",
  },
  {
    icon: Users,
    metric: "16,000+",
    label: { en: "Doctors trust us", bn: "ডাক্তার আমাদের ভরসা করেন" },
    detail: { en: "Across 20+ specialties in Bangladesh.", bn: "বাংলাদেশে ২০+ স্পেশালিটিতে।" },
    accent: "#ea580c",
  },
  {
    icon: ShieldCheck,
    metric: "99.99%",
    label: { en: "Uptime SLA", bn: "আপটাইম SLA" },
    detail: { en: "Enterprise-grade, offline-first architecture.", bn: "এন্টারপ্রাইজ-গ্রেড, অফলাইন-ফার্স্ট।" },
    accent: "#0f172a",
  },
];

export default function OutcomeStatsBento() {
  const { lang } = useLang();
  return (
    <section className="py-20 bg-[#f8fafc]">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#1ba179] px-3 py-1.5 rounded-full bg-white border border-[#c7f1e2] mb-5">
            {lang === "bn" ? "প্রমাণিত ফলাফল" : "Proven outcomes"}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0f172a]">
            {lang === "bn"
              ? "আরও রোগী দেখুন, কম অ্যাডমিন করুন"
              : "Treat more patients, do less admin"}
          </h2>
          <p className="mt-4 text-[15px] text-[#475569]">
            {lang === "bn"
              ? "ডাক্তারদের নিজস্ব রিপোর্ট করা সংখ্যা — DoctorsX চালু করার প্রথম ৯০ দিনের মধ্যে।"
              : "Numbers doctors report themselves — within the first 90 days on DoctorsX."}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {STATS.map((s, i) => {
            const Icon = s.icon;
            const span = s.big ? "lg:col-span-3" : "lg:col-span-2";
            return (
              <div
                key={i}
                className={`${span} group relative overflow-hidden rounded-3xl bg-white border border-[#e2e8f0] p-6 md:p-7 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_rgba(37,195,154,0.35)]`}
              >
                <div
                  className="absolute -top-8 -right-8 w-32 h-32 rounded-full blur-2xl opacity-20 transition-opacity group-hover:opacity-30"
                  style={{ background: s.accent }}
                />
                <div className="relative">
                  <div
                    className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-4"
                    style={{ background: `${s.accent}15`, color: s.accent }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div
                    className={`font-bold tracking-tight leading-none ${
                      s.big ? "text-5xl md:text-6xl" : "text-4xl md:text-5xl"
                    }`}
                    style={{ color: s.accent }}
                  >
                    {s.metric}
                  </div>
                  <div className="mt-3 text-sm font-semibold text-[#0f172a]">
                    {lang === "bn" ? s.label.bn : s.label.en}
                  </div>
                  <p className="mt-1.5 text-[13px] text-[#64748b] leading-relaxed">
                    {lang === "bn" ? s.detail.bn : s.detail.en}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
